import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lightbulb, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const inputCls = "w-full rounded-md border border-line bg-bg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60";
const Field = ({ label, required, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">
      {label}{required && <span className="text-brand-amber ml-0.5">*</span>}
    </div>
    {children}
  </div>
);

export default function SupportFeatureRequest() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    title: "", description: "", problem: "", who_benefits: "",
    importance: "normal", use_case: "", related_feature: "",
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => { api.get("/helpdesk/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Give the feature a title"); return; }
    if (f.description.trim().length < 10) { toast.error("Describe the feature in a bit more detail"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/helpdesk/feature-requests", f);
      toast.success(`Request ${data.reference} logged — thanks for the idea!`);
      navigate(`/dashboard/support/tickets/${data.reference}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit the request"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="feature-request-page">
      <Link to="/dashboard/help" className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary">
        <ArrowLeft className="h-3.5 w-3.5" /> Help Center
      </Link>
      <PageHeader eyebrow="// FEATURE REQUEST" title="Suggest a feature"
        description="Real user ideas drive the roadmap. We review every request — though we can't promise every one gets built." />
      <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-4">
        <Field label="Feature title" required>
          <input className={inputCls} value={f.title} onChange={(e) => set("title")(e.target.value)}
            placeholder="e.g. Auto-post listener shout-outs from WhatsApp" data-testid="fr-title" />
        </Field>
        <Field label="Describe the feature" required>
          <textarea className={`${inputCls} min-h-[100px]`} value={f.description}
            onChange={(e) => set("description")(e.target.value)} data-testid="fr-description" />
        </Field>
        <Field label="What problem would it solve?">
          <textarea className={`${inputCls} min-h-[70px]`} value={f.problem}
            onChange={(e) => set("problem")(e.target.value)} data-testid="fr-problem" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Who would benefit?">
            <input className={inputCls} value={f.who_benefits} onChange={(e) => set("who_benefits")(e.target.value)}
              placeholder="e.g. multi-station owners" data-testid="fr-benefits" />
          </Field>
          <Field label="How important is it to you?">
            <select className={inputCls} value={f.importance} onChange={(e) => set("importance")(e.target.value)} data-testid="fr-importance">
              <option value="low">Nice to have</option>
              <option value="normal">Would use it regularly</option>
              <option value="high">Really need this</option>
              <option value="urgent">Blocking my workflow</option>
            </select>
          </Field>
          <Field label="Related BroadcastFlow feature">
            <select className={inputCls} value={f.related_feature} onChange={(e) => set("related_feature")(e.target.value)} data-testid="fr-related">
              <option value="">None / new area</option>
              {(meta?.features || []).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Example use case">
          <textarea className={`${inputCls} min-h-[70px]`} value={f.use_case}
            onChange={(e) => set("use_case")(e.target.value)} data-testid="fr-usecase"
            placeholder="Walk us through how you'd use it on a normal day…" />
        </Field>
        <div className="flex justify-end pt-2 border-t border-line">
          <button type="button" onClick={submit} disabled={busy} data-testid="fr-submit-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 transition disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lightbulb className="h-4 w-4" />} Submit request
          </button>
        </div>
      </div>
    </div>
  );
}
